import React, { useReducer, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import Cookies from 'js-cookie';

// utils
import { COOKIE_KEYS } from 'js/project/common/util/cookieHelpers';
import analytics, { PAGE_CALLS } from 'js/project/common/util/segmentAnalytics';
import { CountryCode } from 'js/common/utils/countries';
import {
  trackRegistrationData,
  fetchAndShapeClearbit,
  trackRegistrationError as defaultTrackRegistrationError,
  RegistrationError,
  RegistrationAnalyticsData,
  getEmailType,
  ClearbitData,
} from 'js/auth/utils/analyticsUtils';

// styles
import { useViewportSize } from 'js/common/utils/mediaQueries';

// context
import { useRequestParams } from 'js/common/context/RequestParamsContext';

// api
import * as api from 'js/common/services/api';

// services
import * as oktaUtils from 'js/common/utils/oktaUtils';
// eslint-disable-next-line no-restricted-imports
import { updateCsrfHeaders } from 'js/common/services/api/fetchWrapper';
import * as errorHelper from 'js/common/services/authErrorHelper';
import { createInitialOrgAndProject } from 'js/auth/utils/postRegistrationUtils';
import { getMarketplaceRegistrationCookie } from 'js/common/utils/billingHelpers';

// types
import { Search } from 'js/auth/types/search';
import { SignupMethod, getSignUpSource } from 'js/auth/types/signup';
import { FieldNames, FieldPayload } from 'js/auth/types/field';
import { RegexValidation } from 'js/common/types/regexValidation';
import { RegistrationFormFields, ErrorField, ReducerAction } from 'js/auth/types/registration';

// components
import RegistrationForm from './RegistrationForm';
import VercelRegistrationForm from './VercelRegistrationForm';
import Layout from './Layout';
import { ReCaptchaHandles, ReCaptchaResponse } from './ReCaptcha';

interface RequestData extends RegistrationFormFields {
  groupName?: string;
  invitationToken?: string;
  activationCode?: string;
  c?: string;
  kwd?: string;
  jumpId?: string;
  newGroup: boolean;
  anonymousId?: string;
  response?: string;
  clientState: object;
  clearbitData: ClearbitData;
}

interface ReducerState {
  formFields: RegistrationFormFields;
  errorMessage: string;
  errorField: ErrorField;
  captchaError: string;
  isCaptchaBoxChecked: boolean;
  formSubmitted: boolean;
  activationCodeInvalid: boolean;
  inviteTokenInvalid: boolean;
  isSocialSignupDisabled: boolean;
  isRetiredUsername: boolean;
}

interface RegistrationPageProps {
  location?: Pick<Location, 'search'>;
  windowLocation?: Pick<Location, 'assign' | 'href'>;
  oktaSessionExists: () => Promise<boolean>;
  trackRegistration?: (obj: object) => void;
  trackRegistrationError?: (obj: RegistrationError) => void;
  match?: RouteComponentProps;
}

// These validations are based off those done on the backend API - see: https://tinyurl.com/h3rn8ja3
const fieldValidationMap: { [key in FieldNames]?: RegexValidation } = {
  [FieldNames.USERNAME]: {
    regExp: /^.{1,}$/,
    errorMessage: 'Email Address must not be empty',
  },
  [FieldNames.FIRST_NAME]: {
    regExp: /^.{1,20}$/,
    errorMessage: 'First name must be between 1 and 20 characters long',
  },
  [FieldNames.LAST_NAME]: {
    regExp: /^.{1,20}$/,
    errorMessage: 'Last name must be between 1 and 20 characters long',
  },
  [FieldNames.PHONE_NUMBER]: {
    regExp: /^.{1,}$/,
    errorMessage: 'Phone number must not be empty',
  },
  [FieldNames.COMPANY]: {
    regExp: /^.{1,}$/,
    errorMessage: 'Company Name must not be empty',
  },
};

const reducerState: ReducerState = {
  formFields: {
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    company: '',
    tos: false,
    honeypot: '',
    jobResponsibility: '',
    country: '',
  },
  errorMessage: '',
  errorField: '',
  captchaError: '',
  isCaptchaBoxChecked: false,
  formSubmitted: false,
  activationCodeInvalid: false,
  inviteTokenInvalid: false,
  isSocialSignupDisabled: true,
  isRetiredUsername: false,
};

function registrationReducer(state: ReducerState, action: ReducerAction): ReducerState {
  switch (action.type) {
    case 'formSubmitted': {
      return {
        ...state,
        formSubmitted: true,
      };
    }
    case 'reducerField': {
      const { field, value } = action.payload as FieldPayload;
      return {
        ...state,
        [field]: value,
        errorMessage: '', // reset the error message and errorField on every input change
        errorField: '',
      };
    }
    case 'field': {
      const { field, value } = action.payload as FieldPayload;
      return {
        ...state,
        formFields: {
          ...state.formFields,
          [field]: value,
        },
        errorMessage: '', // reset the error message and errorField on every input change
        errorField: '',
      };
    }
    case 'setError': {
      const errorCode = action.payload;
      const errorCodeStr = errorCode?.toString() ?? '';
      let retiredUsernameError = false;
      let errorFieldName: ErrorField = '';

      if (errorCodeStr === 'REGISTER_WITH_RETIRED_USERNAME') {
        retiredUsernameError = true;
      } else if (errorCodeStr.includes('PASSWORD')) {
        errorFieldName = FieldNames.PASSWORD;
      } else if (errorCodeStr.includes('USERNAME') || errorCodeStr.includes('EMAIL')) {
        errorFieldName = FieldNames.USERNAME;
      } else if (errorCodeStr === 'INVALID_GROUP_NAME') {
        errorFieldName = FieldNames.COMPANY;
      } else if (errorCodeStr.includes('FIRST_NAME')) {
        errorFieldName = FieldNames.FIRST_NAME;
      } else if (errorCodeStr.includes('LAST_NAME')) {
        errorFieldName = FieldNames.LAST_NAME;
      } else if (errorCodeStr.includes('PHONE_NUMBER')) {
        errorFieldName = FieldNames.PHONE_NUMBER;
      }

      return {
        ...state,
        errorMessage: errorHelper.getErrorMessageFromCodeForRegistration(errorCode),
        errorField: errorFieldName,
        formSubmitted: false,
        isCaptchaBoxChecked: false,
        isRetiredUsername: retiredUsernameError,
      };
    }
    case 'setInvalidActivationCode': {
      return {
        ...state,
        activationCodeInvalid: true,
      };
    }
    case 'setInvalidInviteToken': {
      return {
        ...state,
        inviteTokenInvalid: true,
      };
    }
    case 'processCaptchaError': {
      return {
        ...state,
        captchaError: errorHelper.getErrorMessageFromCodeForRegistration(action.payload),
        isCaptchaBoxChecked: false,
      };
    }
    case 'setCaptchaBoxChecked': {
      return {
        ...state,
        isCaptchaBoxChecked: action.payload,
      };
    }
    default:
      return state;
  }
}

export default function RegistrationPage({
  location: { search } = { search: '' },
  oktaSessionExists = oktaUtils.oktaSessionExists,
  windowLocation = window.location,
  trackRegistration = trackRegistrationData,
  trackRegistrationError = defaultTrackRegistrationError,
  match: { params } = { params: '' },
}: RegistrationPageProps) {
  const {
    onPrem,
    hasUsers,
    segmentDefaultUserId,
    reCaptchaEnabledRegistration,
    isGoogleAuthEnabled,
    isGithubAuthEnabled,
    eloquaSiteId,
    analyticsEnabled,
    userRegistrationEnabled,
    userRegistrationDisabledRedirectUrl,
    registrationExtraFields,
    registrationDefaultCountry,
    vercelEmail,
    vercelUserId,
    vercelTeamName,
    vercelTeamId,
  } = useRequestParams();

  const parsedSearchLocation = queryString.parse(search) as Search;
  const {
    inviteToken,
    activationCode,
    groupname,
    orgname,
    username: invitedUsername,
    fromURI,
    jmp,
    c,
    kwd,
    vercelEmail: vercelEmailFromClient,
    vercelUserId: vercelUserIdFromClient,
    vercelTeamId: vercelTeamIdFromClient,
    vercelTeamName: vercelTeamnameFromClient,
  } = parsedSearchLocation;

  const isVercelIntegration = params.integration && params.integration === 'vercel';
  const clientState = {
    ...parsedSearchLocation,
    ...(isVercelIntegration && {
      n: '/account/vercel/org/selection',
      isVercelIntegration: true,
      vercelEmail: vercelEmail ?? vercelEmailFromClient,
      vercelUserId: vercelUserId ?? vercelUserIdFromClient,
      vercelTeamId: vercelTeamId ?? vercelTeamIdFromClient,
      vercelTeamName: vercelTeamName ?? vercelTeamnameFromClient,
    }),
  };
  const [
    {
      formFields,
      errorMessage,
      captchaError,
      errorField,
      formSubmitted,
      isCaptchaBoxChecked,
      inviteTokenInvalid,
      activationCodeInvalid,
      isSocialSignupDisabled,
      isRetiredUsername,
    },
    dispatch,
  ] = useReducer(registrationReducer, reducerState, (state) => {
    const prefilledFormFields: Partial<RegistrationFormFields> = {};

    if (!isVercelIntegration) {
      if (registrationExtraFields.includes(FieldNames.COUNTRY) && registrationDefaultCountry) {
        prefilledFormFields.country = registrationDefaultCountry;
      }
      if (inviteToken && invitedUsername) {
        prefilledFormFields.username = invitedUsername;
      }
    } else {
      prefilledFormFields.username = vercelEmail ?? vercelEmailFromClient;
    }

    return {
      ...state,
      formFields: {
        ...state.formFields,
        ...prefilledFormFields,
      },
    };
  });

  const { username, password, firstName, lastName, company, tos, honeypot, phoneNumber, jobResponsibility, country } =
    formFields;

  const isInviteFlow = inviteToken !== undefined && inviteToken !== '';
  const hasGroupName = groupname !== undefined && groupname !== '';
  const hasOrgName = orgname !== undefined && orgname !== '';
  const isSmallViewport = useViewportSize('small');

  const marketplaceRegistrationCookie = getMarketplaceRegistrationCookie();
  const marketplaceType = marketplaceRegistrationCookie?.partnerType;
  const analyticsSignupSource = getSignUpSource(marketplaceType, isVercelIntegration);

  const reCaptchaRef = useRef<ReCaptchaHandles>(null);
  const [clearbitData, setClearbitData] = useState({});

  /**
   * Validates a value of a field against its predefined regex.
   * If no predefined regex exists for the field, the value of the field will be validated as any valid string.
   *
   * @param fieldName The name of the field as it exists in the FieldNames enum
   * @returns a boolean indicating whether or not the value satisfies the defined regex for the field
   */
  function isUserInputValid(fieldName: FieldNames): boolean {
    // Default regex validation for user input is "any string"
    const regexForField: RegExp = fieldValidationMap[fieldName]?.regExp || /\S/;
    return regexForField.test(formFields[fieldName]);
  }

  useEffect(() => {
    async function checkIfOktaSessionExists() {
      let sessionExists;
      try {
        sessionExists = await oktaSessionExists();
      } catch (e) {
        // ignore errors
      }
      if (sessionExists) {
        await oktaUtils.endOktaSession();
      }
      dispatch({ type: 'reducerField', payload: { field: FieldNames.IS_SOCIAL_SIGNUP_DISABLED, value: false } });
    }

    if (!onPrem) {
      checkIfOktaSessionExists();
    } else {
      dispatch({ type: 'reducerField', payload: { field: FieldNames.IS_SOCIAL_SIGNUP_DISABLED, value: false } });
    }
  }, [onPrem, oktaSessionExists]);

  useEffect(() => {
    if (analyticsEnabled && !onPrem) {
      analytics.page(PAGE_CALLS.REGISTRATION, {
        signup_source: analyticsSignupSource,
        isFromMp: !!marketplaceRegistrationCookie,
        marketplaceSource: marketplaceRegistrationCookie?.partnerType ?? '',
      });
    }
  }, [analyticsEnabled, onPrem]);

  useEffect(() => {
    async function fetchClearbit() {
      const fetchedClearbitData = await fetchAndShapeClearbit();
      setClearbitData(fetchedClearbitData);
      Cookies.set(COOKIE_KEYS.CLEARBIT_DATA, fetchedClearbitData);
    }
    if (analyticsEnabled) {
      fetchClearbit();
    }
  }, []);

  useEffect(() => {
    async function verifyActivationCode() {
      try {
        await api.billing.getAvailableActivationCode({ activationCode });
      } catch ({ errorCode }) {
        if (userRegistrationDisabledRedirectUrl) {
          windowLocation.href = userRegistrationDisabledRedirectUrl;
        } else {
          dispatch({ type: 'setInvalidActivationCode' });
        }
      }
    }

    async function verifyInvite() {
      try {
        await api.user.inviteGet({ username: invitedUsername, inviteToken });
      } catch ({ errorCode }) {
        if (userRegistrationDisabledRedirectUrl) {
          windowLocation.href = userRegistrationDisabledRedirectUrl;
        } else {
          dispatch({ type: 'setInvalidInviteToken' });
        }
      }
    }

    if (!userRegistrationEnabled) {
      // When self-serve registration is off, users should bear an invite token or activation code in order
      // to register. In case they do not (or if the codes are invalid), and the app environment requires
      // the user to be navigated away otherwise (e.g.: FedRAMP), redirect based on the conf property value
      if (activationCode) {
        verifyActivationCode();
      } else if (inviteToken) {
        verifyInvite();
      } else if (userRegistrationDisabledRedirectUrl) {
        windowLocation.href = userRegistrationDisabledRedirectUrl;
      }
    }
  }, [
    userRegistrationEnabled,
    userRegistrationDisabledRedirectUrl,
    invitedUsername,
    inviteToken,
    activationCode,
    windowLocation,
  ]);

  const trackAnalytics = ({ anonymousId }: { anonymousId?: string }) => {
    if (onPrem || !analyticsEnabled) {
      return;
    }

    const data: RegistrationAnalyticsData = {
      first_name: firstName,
      last_name: lastName,
      email: username,
      signup_source: analyticsSignupSource,
      signup_method: SignupMethod.FORM,
    };

    if (registrationExtraFields.includes(FieldNames.COMPANY)) {
      data.company = company;
    }
    if (registrationExtraFields.includes(FieldNames.COUNTRY)) {
      data.country = country as CountryCode;
    }
    if (registrationExtraFields.includes(FieldNames.JOB_RESPONSIBILITY)) {
      data.job_function = jobResponsibility;
    }
    if (registrationExtraFields.includes(FieldNames.PHONE_NUMBER)) {
      data.phone = phoneNumber;
    }

    trackRegistration({
      eloquaSiteId,
      parsedSearchLocation,
      anonymousId,
      userId: segmentDefaultUserId,
      data,
      clearbitData,
      email_type: getEmailType(username),
    });
  };

  const trackError = ({ errorCode, errorMessage: message }: RegistrationError) => {
    trackRegistrationError({
      errorCode,
      errorMessage: message,
      signUpSource: analyticsSignupSource,
      signUpMethod: SignupMethod.FORM,
    });
  };

  const generateClientState = (): RequestData['clientState'] => {
    const defaultClientState = {
      // Keep incoming queryParams
      ...parsedSearchLocation,
      n: '/account/register/success',
      firstName,
      fromURI,
      signupSource: analyticsSignupSource,
      signupMethod: SignupMethod.FORM,
    };

    if (registrationExtraFields.includes(FieldNames.COMPANY)) {
      defaultClientState[FieldNames.COMPANY] = company;
    }

    if (isVercelIntegration) {
      return {
        ...defaultClientState,
        n: '/account/vercel/confirm', // send the user to confirmation page for Vercel Integration
        vercelEmail: vercelEmail ?? vercelEmailFromClient,
        vercelUserId: vercelUserId ?? vercelUserIdFromClient,
        vercelTeamId: vercelTeamId ?? vercelTeamIdFromClient,
        vercelTeamName: vercelTeamName ?? vercelTeamnameFromClient,
        isNewVercelOrg: 'true',
        isVercelIntegration,
      };
    }
    return defaultClientState;
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: 'formSubmitted' });

    let registerCallResponse;

    const clientState: RequestData['clientState'] = generateClientState();

    const requestData: RequestData = {
      ...formFields,
      activationCode,
      c,
      kwd,
      jumpId: jmp,
      newGroup: !hasGroupName && !hasOrgName,
      honeypot: honeypot || null,
      clientState,
      clearbitData,
    };

    // For the invite flow, username and groupname are provided
    if (isInviteFlow) {
      requestData.groupName = groupname;
      requestData.invitationToken = inviteToken;
    }

    // Verify that the user is not robot if reCaptcha is enabled
    if (reCaptchaEnabledRegistration) {
      const { errorCode, response } = (reCaptchaRef.current && reCaptchaRef.current.verify()) as ReCaptchaResponse;

      requestData.response = response;
      if (errorCode !== '') {
        dispatch({ type: 'processCaptchaError', payload: errorCode });
        trackError({ errorCode });
        return;
      }
    }

    requestData.anonymousId = analytics.user()?.anonymousId();
    try {
      registerCallResponse = await api.user.userRegisterCall(requestData);
    } catch ({ errorCode, message }) {
      dispatch({ type: 'setError', payload: errorCode });

      if (reCaptchaEnabledRegistration && reCaptchaRef.current) {
        reCaptchaRef.current.reset();
      }

      trackError({ errorCode, errorMessage: message });
      return;
    }

    trackAnalytics({ anonymousId: requestData.anonymousId });

    const { loginRedirect, csrfToken, csrfTime } = registerCallResponse;
    updateCsrfHeaders({ csrfTime, csrfToken });

    // If using okta, sessionToken will be returned from userRegisterCall
    if (loginRedirect) {
      windowLocation.assign(loginRedirect);
      return;
    }

    /*
     For Onprem, the org and project should be created as part of the registration process, if the
     user isn't already being invited. Then let the backend handle where to redirect the user
    */
    if (onPrem) {
      if (isInviteFlow) {
        windowLocation.assign('/');
      } else {
        try {
          await createInitialOrgAndProject({ isOnPrem: onPrem });
        } catch (exception) {
          console.log('Some error happened creating initial org and project', exception);
        } finally {
          windowLocation.assign('/');
        }
      }
    }
  };

  const isSubmitDisabled = isVercelIntegration
    ? formSubmitted ||
      !(
        isUserInputValid(FieldNames.USERNAME) &&
        isUserInputValid(FieldNames.FIRST_NAME) &&
        isUserInputValid(FieldNames.LAST_NAME) &&
        tos &&
        password.length > 0 &&
        errorMessage.length === 0
      )
    : (!userRegistrationEnabled && !(activationCode ?? inviteToken)) ||
      inviteTokenInvalid ||
      activationCodeInvalid ||
      formSubmitted ||
      (reCaptchaEnabledRegistration && !isCaptchaBoxChecked) ||
      !(
        isUserInputValid(FieldNames.USERNAME) &&
        isUserInputValid(FieldNames.FIRST_NAME) &&
        isUserInputValid(FieldNames.LAST_NAME) &&
        password.length > 0 &&
        // for on-prem company is not shown
        (!registrationExtraFields.includes(FieldNames.COMPANY) || isUserInputValid(FieldNames.COMPANY)) &&
        // for on-prem jobResponsibility is not shown
        (!registrationExtraFields.includes(FieldNames.JOB_RESPONSIBILITY) || jobResponsibility.length > 0) &&
        // for on-prem country is not shown
        (!registrationExtraFields.includes(FieldNames.COUNTRY) || country.length > 0) &&
        // for on-prem phone number is not shown
        (!registrationExtraFields.includes(FieldNames.PHONE_NUMBER) || isUserInputValid(FieldNames.PHONE_NUMBER)) &&
        // for on-prem only first user sees tos
        (onPrem && hasUsers ? true : tos) &&
        errorMessage.length === 0
      );

  const vercelFormProps = {
    username,
    password,
    firstName,
    lastName,
    tos,
    search,
    fieldValidationMap,
    formSubmitted,
    errorField,
    errorMessage,
    isSubmitDisabled,
    dispatch,
    isGoogleAuthEnabled,
    isSocialSignupDisabled,
    isGithubAuthEnabled,
    clientState,
    onSubmit: (e) => onFormSubmit(e),
  };

  const registrationFormProps = {
    ...vercelFormProps,
    isGoogleAuthEnabled,
    isSocialSignupDisabled,
    isGithubAuthEnabled,
    groupname,
    orgname,
    phoneNumber,
    company,
    country,
    jobResponsibility,
    honeypot,
    isRetiredUsername,
    isInviteFlow,
    isSubmitDisabled,
    isSmallViewport,
    hasUsers,
    userRegistrationEnabled,
    onPrem,
    inviteToken,
    activationCode,
    reCaptchaEnabledRegistration,
    reCaptchaRef,
    captchaError,
    registrationExtraFields,
    marketplaceRegistrationCookie,
  };

  if (isVercelIntegration) {
    return <VercelRegistrationForm {...vercelFormProps} />;
  }
  return (
    <>
      <Helmet title="Create Account" />
      <Layout contentPlacement="right">
        <RegistrationForm {...registrationFormProps} />
      </Layout>
    </>
  );
}
